import { useEffect, useSyncExternalStore } from 'react';
import { exportReminderOf, type ExportReminder } from '../lib/exportReminder';
import {
  readReminderTimes,
  recordFirstUse,
  reminderSnapshot,
  snoozeExportReminder,
  subscribeReminder,
} from '../lib/exportReminderStore';
import { useLocalMode } from './useLocalMode';
import { useSyncEnabled } from './useSyncStatus';
import { useNow } from './useNow';
import { useServices } from './useServices';

export interface ExportReminderState {
  /** The reminder to show; null when none is due. */
  readonly reminder: ExportReminder | null;
  /** "Later": stay quiet for a week. */
  readonly snooze: () => void;
  /** Epoch ms of the last full export on this device; null when there was none. */
  readonly lastExportAt: number | null;
  /** Local mode: the data exists only in this browser, so the reminder says so and offers "Create account". */
  readonly localMode: boolean;
}

/** The export reminder for this device (never shown while sync keeps a second copy). */
export function useExportReminder(): ExportReminderState {
  const { now } = useServices();
  // Re-render whenever one of the stored timestamps changes; the values themselves are read below.
  useSyncExternalStore(subscribeReminder, reminderSnapshot, () => '');
  const nowMs = useNow(10 * 60_000);
  const syncEnabled = useSyncEnabled();
  const localMode = useLocalMode();

  useEffect(() => recordFirstUse(now()), [now]);

  const times = readReminderTimes();
  const reminder = exportReminderOf({ ...times, nowMs, syncEnabled });
  return { reminder, snooze: () => snoozeExportReminder(now()), lastExportAt: times.lastExportAt, localMode };
}
